import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useLayoutEffect,
  useRef,
  ReactNode,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { FormData, FormContextValue } from '../types/form';
import { initialFormData } from '../constants/formInitialData';
import { normalizeFormData } from '../utils/normalizeFormData';
import {
  resolveClientGuid,
  getApplicationGuidFromStorage,
  setApplicationGuidInStorage,
  clearApplicationGuidFromStorage,
  clearClientGuidFromStorage,
} from '../utils/sessionIds';
import * as applicationApi from '../services/applicationApi';

const APPLICANT_OCCUPANT_TYPE_KEY = 'applicant_occupant_type';

const FormContext = createContext<FormContextValue | undefined>(undefined);

export const useForm = (): FormContextValue => {
  const context = useContext(FormContext);
  if (context === undefined) {
    throw new Error('useForm must be used within a FormProvider');
  }
  return context;
};

export const getApplicantOccupantType = (): string => {
  return localStorage.getItem(APPLICANT_OCCUPANT_TYPE_KEY) || '';
};

export const setApplicantOccupantType = (value: string): void => {
  if (value) {
    localStorage.setItem(APPLICANT_OCCUPANT_TYPE_KEY, value);
  } else {
    localStorage.removeItem(APPLICANT_OCCUPANT_TYPE_KEY);
  }
};

interface FormProviderProps {
  children: ReactNode;
}

function readClientGuidFromBrowserLocation(): string | null {
  if (typeof window === 'undefined') return null;
  return resolveClientGuid(
    window.location.pathname,
    new URLSearchParams(window.location.search)
  );
}

function FormProviderInner({ children }: FormProviderProps): JSX.Element {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const [clientGuid, setClientGuid] = useState(() => readClientGuidFromBrowserLocation());

  useLayoutEffect(() => {
    setClientGuid(resolveClientGuid(pathname, searchParams));
  }, [pathname, searchParams]);

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [applicationGuid, setApplicationGuid] = useState<string | null>(() =>
    getApplicationGuidFromStorage()
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [draftReady, setDraftReady] = useState(false);
  const [draftCurrentStep, setDraftCurrentStep] = useState<string | null>(null);
  const ensureDraftRun = useRef<Promise<void> | null>(null);

  const updateFormData = useCallback((newData: Partial<FormData>): void => {
    setFormData((prevData) => ({
      ...prevData,
      ...newData,
    }));
  }, []);

  const ensureDraft = useCallback(async (): Promise<void> => {
    if (ensureDraftRun.current) {
      return ensureDraftRun.current;
    }
    const run = (async (): Promise<void> => {
      setSaveError(null);
      setDraftReady(false);
      const cg = resolveClientGuid(pathname, searchParams);
      setClientGuid(cg);
      if (!cg) {
        navigate('/oops', { replace: true, state: { reason: 'client_not_found' } });
        setDraftReady(false);
        return;
      }

      const normalizeClient = (s: string): string => s.trim().toLowerCase();
      const currentClient = normalizeClient(cg);

      let guid = getApplicationGuidFromStorage();

      if (guid) {
        try {
          const app = await applicationApi.getApplication(guid);
          const appClient = app.clientGuid != null ? normalizeClient(app.clientGuid) : '';
          if (!appClient || appClient !== currentClient) {
            clearApplicationGuidFromStorage();
            setApplicationGuid(null);
            setFormData(initialFormData);
            guid = null;
          } else {
            setApplicationGuid(app.guid);
            setFormData(normalizeFormData(app.formData));
            setDraftCurrentStep(app.currentStep ?? 'application');
            setDraftReady(true);
            return;
          }
        } catch (err) {
          const oops = applicationApi.getOopsRedirectReason(err);
          if (oops) {
            navigate('/oops', { replace: true, state: { reason: oops } });
            setDraftReady(false);
            return;
          }
          clearApplicationGuidFromStorage();
          guid = null;
        }
      }

      try {
        const created = await applicationApi.createApplication({
          clientGuid: cg,
          currentStep: 'application',
        });
        setApplicationGuid(created.guid);
        setApplicationGuidInStorage(created.guid);
        setFormData(normalizeFormData(created.formData));
        setDraftCurrentStep(created.currentStep ?? 'application');
        setDraftReady(true);
      } catch (err) {
        const oops = applicationApi.getOopsRedirectReason(err);
        if (oops) {
          navigate('/oops', { replace: true, state: { reason: oops } });
          setDraftReady(false);
          return;
        }
        setSaveError(applicationApi.getErrorMessage(err));
        setDraftReady(false);
      }
    })();
    ensureDraftRun.current = run;
    try {
      await run;
    } finally {
      ensureDraftRun.current = null;
    }
  }, [navigate, pathname, searchParams]);

  const persistDraft = useCallback(
    async (next: FormData, currentStep: string): Promise<void> => {
      const guid = applicationGuid ?? getApplicationGuidFromStorage();
      if (!guid) {
        setSaveError('errors.noSavedApplication');
        return;
      }
      setIsSaving(true);
      setSaveError(null);
      try {
        await applicationApi.patchApplication(guid, {
          ...next,
          currentStep,
        });
        setDraftCurrentStep(currentStep);
      } catch (err) {
        const oops = applicationApi.getOopsRedirectReason(err);
        if (oops) {
          clearApplicationGuidFromStorage();
          navigate('/oops', { replace: true, state: { reason: oops } });
          throw err;
        }
        setSaveError(applicationApi.getErrorMessage(err));
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [applicationGuid, navigate]
  );

  const clearSessionAfterApplicationComplete = useCallback((): void => {
    clearApplicationGuidFromStorage();
    clearClientGuidFromStorage();
    setApplicantOccupantType('');
    setApplicationGuid(null);
    setFormData(initialFormData);
    setDraftCurrentStep(null);
    setDraftReady(false);
    setSaveError(null);
    const cg = resolveClientGuid(pathname, searchParams);
    setClientGuid(cg);
  }, [pathname, searchParams]);

  const value = useMemo<FormContextValue>(
    () => ({
      formData,
      updateFormData,
      clientGuid,
      applicationGuid,
      persistDraft,
      ensureDraft,
      clearSessionAfterApplicationComplete,
      draftReady,
      draftCurrentStep,
      isSaving,
      saveError,
    }),
    [
      formData,
      updateFormData,
      clientGuid,
      applicationGuid,
      persistDraft,
      ensureDraft,
      clearSessionAfterApplicationComplete,
      draftReady,
      draftCurrentStep,
      isSaving,
      saveError,
    ]
  );

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
}

export const FormProvider = ({ children }: FormProviderProps): JSX.Element => {
  return <FormProviderInner>{children}</FormProviderInner>;
};
